import { AUTH_REFRESH } from './endpoints'
import { clearAuth, loadAuth, updateAccessToken } from './authStorage'

type ApiErrorPayload = {
  status?: number
  message?: string
  error?: string
}

export class ApiError extends Error {
  status?: number
  payload?: unknown

  constructor(message: string, status?: number, payload?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

export class ContactLimitError extends ApiError {
  limit: number
  currentUsage: number
  upgradeUrl?: string

  constructor(limit: number, currentUsage: number, upgradeUrl?: string) {
    super(`Contact limit reached (${currentUsage}/${limit})`, 402)
    this.name = 'ContactLimitError'
    this.limit = limit
    this.currentUsage = currentUsage
    this.upgradeUrl = upgradeUrl
  }
}

const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

function joinUrl(base: string, path: string) {
  if (!base) return path
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`
}

function authHeaders(): Record<string, string> {
  const auth = loadAuth()
  if (auth?.accessToken) {
    return { Authorization: `Bearer ${auth.accessToken}` }
  }
  return {}
}

async function parseResponse(res: Response): Promise<unknown> {
  if (res.status === 204) return null
  const contentType = res.headers.get('content-type') ?? ''
  if (contentType.includes('application/json')) {
    return (await res.json().catch(() => null)) as unknown
  }
  const text = await res.text().catch(() => '')
  if (!res.ok) return text
  if (text.length === 0) return null
  throw new ApiError(
    `Expected JSON response but received '${contentType || 'unknown content-type'}'. ` +
      `This usually means VITE_API_URL is misconfigured and the request was served by the SPA.`,
    res.status,
    text.slice(0, 200),
  )
}

function throwIfError(res: Response, payload: unknown): void {
  if (!res.ok) {
    if (res.status === 402) {
      const p402 = payload as { feature?: string; limit?: number; currentUsage?: number; upgradeUrl?: string } | null
      if (p402?.feature === 'CONTACT_LIMIT' && p402.limit !== undefined && p402.currentUsage !== undefined) {
        throw new ContactLimitError(p402.limit, p402.currentUsage, p402.upgradeUrl)
      }
    }
    const p = payload as ApiErrorPayload | null
    const msg = p?.message ?? p?.error ?? `Request failed (${res.status})`
    throw new ApiError(msg, res.status, payload)
  }
}

// Mutex: only one refresh in flight at a time. All concurrent 401s wait on the same promise.
let refreshPromise: Promise<void> | null = null

async function attemptTokenRefresh(): Promise<void> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    const auth = loadAuth()
    if (!auth?.refreshToken) {
      clearAuth()
      window.location.href = '/signin'
      throw new ApiError('No refresh token available', 401)
    }

    const res = await fetch(joinUrl(API_BASE_URL, AUTH_REFRESH), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: auth.refreshToken }),
    })

    if (!res.ok) {
      clearAuth()
      window.location.href = '/signin'
      throw new ApiError('Session expired. Please sign in again.', 401)
    }

    const data = (await res.json()) as { accessToken: string }
    updateAccessToken(data.accessToken)
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

// Core fetch helper: injects auth headers, retries once after a successful token refresh.
async function fetchWithAuth(url: string, init: RequestInit, isRetry = false): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      ...authHeaders(),
      ...(init.headers ?? {}),
    },
  })

  if (res.status === 401 && !isRetry) {
    await attemptTokenRefresh()
    return fetchWithAuth(url, init, true)
  }

  if (res.headers.get('X-Nudge-Plan-Warning')) {
    window.dispatchEvent(new CustomEvent('nudge:plan-warning'))
  }

  return res
}

export async function apiGet<TResponse>(path: string, init?: RequestInit): Promise<TResponse> {
  const res = await fetchWithAuth(joinUrl(API_BASE_URL, path), {
    ...init,
    method: 'GET',
  })
  const payload = await parseResponse(res)
  throwIfError(res, payload)
  return payload as TResponse
}

export async function apiPost<TResponse, TBody extends Record<string, unknown>>(
  path: string,
  body: TBody,
  init?: RequestInit,
): Promise<TResponse> {
  const res = await fetchWithAuth(joinUrl(API_BASE_URL, path), {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  })
  const payload = await parseResponse(res)
  throwIfError(res, payload)
  return payload as TResponse
}

export async function apiPut<TResponse, TBody extends Record<string, unknown>>(
  path: string,
  body: TBody,
  init?: RequestInit,
): Promise<TResponse> {
  const res = await fetchWithAuth(joinUrl(API_BASE_URL, path), {
    ...init,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  })
  const payload = await parseResponse(res)
  throwIfError(res, payload)
  return payload as TResponse
}

export async function apiDelete<TResponse = void>(path: string, init?: RequestInit): Promise<TResponse> {
  const res = await fetchWithAuth(joinUrl(API_BASE_URL, path), {
    ...init,
    method: 'DELETE',
  })
  const payload = await parseResponse(res)
  throwIfError(res, payload)
  return payload as TResponse
}

export async function apiPatch<TResponse, TBody extends Record<string, unknown>>(
  path: string,
  body: TBody,
  init?: RequestInit,
): Promise<TResponse> {
  const res = await fetchWithAuth(joinUrl(API_BASE_URL, path), {
    ...init,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    body: JSON.stringify(body),
  })
  const payload = await parseResponse(res)
  throwIfError(res, payload)
  return payload as TResponse
}
